import { skipToken, type UseQueryResult } from '@tanstack/react-query';
import type { CurrentUserQuery } from 'generated/gql/account-manager/graphql';
import { useAmQuery } from 'graphql/useAmQuery';
import {
  isCustomerType,
  isInterval,
  type CustomerType,
  type Interval,
} from 'types/Users';
import type { Maybe } from 'types/Utilities';
import { currentUserQueryDocument } from './useCurrentUser.graphql.am';
import { useJwt, userDataFromToken, type CarrotTokenUserData } from './useJwt';

/** We modify the query results for convenience */
type CurrentUser = Omit<
  NonNullable<CurrentUserQuery['user']>,
  'id' | 'createdAt'
> & {
  accountManagerUserId: number;
  /** @deprecated We are moving to URL-based account selection. */
  activeAccount: NonNullable<CurrentUserQuery['user']>['activeAccount'];
  avatarUrl: CarrotTokenUserData['avatarUrl'] | undefined;
  createdAt: string;
  /** This is copied from currentSubscription as a convenience */
  customerType: CustomerType | null;
  /** This is copied from currentSubscription as a convenience */
  interval: Interval | null;
  /** Pricing and packaging version. This is copied from currentSubscription as a convenience. */
  packagingVersion: Maybe<number>;
};

type UseCurrentUserQueryResult = UseQueryResult<CurrentUserData>;
type CurrentUserData = { user?: CurrentUser };

function useCurrentUser(): UseCurrentUserQueryResult {
  const { data } = useJwt();
  const userData = userDataFromToken(data?.token);
  const wordpressUserId = Number(userData?.id);

  return useAmQuery(
    currentUserQueryDocument,
    wordpressUserId ? { wordpressUserId } : skipToken,
    {
      select: (data) => transformCurrentUser(data, userData?.avatarUrl),
    }
  );
}

/** Make the returned user query data more convenient to use */
function transformCurrentUser(
  data: CurrentUserQuery,
  avatarUrl: Maybe<string>
): CurrentUserData {
  if (!data.user) {
    return { user: undefined };
  }

  const starter = data.user?.currentSubscription?.starter;
  const customerType = starter?.customerType;
  const interval = starter?.interval;

  return {
    user: {
      ...data.user,
      accountManagerUserId: data.user.id,
      activeAccount: data.user.activeAccount,
      avatarUrl,
      customerType: isCustomerType(customerType) ? customerType : null,
      interval: isInterval(interval) ? interval : null,
      packagingVersion: starter?.version,
    },
  };
}

/**
 * WARN: only use this in places where the user is guaranteed to exist,
 * e.g., pages that won't be rendered unless the account has passed the checks
 * in AuthorizedPage.
 *
 * @throws {Error} if the user is not found
 */
function useGuaranteedCurrentUser() {
  const userQuery = useCurrentUser();

  const user = userQuery.data?.user;
  if (!user) {
    throw new Error('A user is required to use this hook');
  }

  return user;
}

export { useCurrentUser, useGuaranteedCurrentUser };
export type { CurrentUser, UseCurrentUserQueryResult };
